import { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { FundPriceSnapshot } from '../api';

type RangeKey = '1M' | '6M' | '1Y' | '5Y' | 'ALL';

const RANGES: { key: RangeKey; label: string; days: number | null }[] = [
  { key: '1M', label: '1M', days: 30 },
  { key: '6M', label: '6M', days: 183 },
  { key: '1Y', label: '1Y', days: 365 },
  { key: '5Y', label: '5Y', days: 365 * 5 },
  { key: 'ALL', label: 'All', days: null },
];

export function FundPriceChart({
  prices,
  markerDate,
  markerLabel,
}: {
  prices: FundPriceSnapshot[];
  markerDate?: string;
  markerLabel?: string;
}) {
  const [range, setRange] = useState<RangeKey>('1Y');

  const filtered = useMemo(() => {
    const cfg = RANGES.find((r) => r.key === range)!;
    if (cfg.days == null) return prices;
    const cutoffMs = Date.now() - cfg.days * 24 * 60 * 60 * 1000;
    return prices.filter((p) => Date.parse(p.date) >= cutoffMs);
  }, [prices, range]);

  // ReferenceLine x must match a data-point's category value — find the
  // first snapshot on or after the marker date that's actually in the
  // visible range. Returns undefined when the marker falls outside (chart
  // simply omits the line).
  const markerSnapDate = useMemo(() => {
    if (!markerDate || filtered.length === 0) return undefined;
    const targetMs = Date.parse(markerDate);
    if (Number.isNaN(targetMs)) return undefined;
    const hit = filtered.find((p) => Date.parse(p.date) >= targetMs);
    return hit?.date;
  }, [filtered, markerDate]);

  if (prices.length === 0) {
    return (
      <p style={{ color: '#888', margin: 0 }}>
        No price history yet — click Refresh on the funds list to backfill.
      </p>
    );
  }

  const currency = filtered[0]?.currency ?? prices[0]?.currency;
  const data = filtered.map((p) => ({
    date: p.date,
    nav: p.nav_pence,
  }));

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.5rem' }}>
        {RANGES.map((r) => {
          const active = r.key === range;
          const enabled =
            r.days == null ||
            Date.parse(prices[0].date) <= Date.now() - r.days * 24 * 60 * 60 * 1000 + 86400000;
          return (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              disabled={!enabled}
              style={{
                padding: '0.2rem 0.6rem',
                fontSize: '0.8rem',
                background: active ? '#3b82f6' : '#f4f4f5',
                color: active ? '#fff' : '#333',
                border: '1px solid',
                borderColor: active ? '#3b82f6' : '#ddd',
                borderRadius: 4,
                cursor: enabled ? 'pointer' : 'not-allowed',
                opacity: enabled ? 1 : 0.4,
              }}
            >
              {r.label}
            </button>
          );
        })}
      </div>
      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="navGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#666' }}
              minTickGap={40}
              tickFormatter={fmtAxisDate}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#666' }}
              domain={['auto', 'auto']}
              tickFormatter={(v: number) => fmtAxisPrice(v, currency)}
              width={60}
            />
            <Tooltip
              formatter={(v) => [fmtTooltipPrice(Number(v), currency), 'NAV']}
              labelFormatter={(l) => String(l)}
              contentStyle={{ fontSize: '0.85rem' }}
            />
            <Area
              type="monotone"
              dataKey="nav"
              stroke="#3b82f6"
              strokeWidth={2}
              fill="url(#navGradient)"
              isAnimationActive={false}
            />
            {markerSnapDate && (
              <ReferenceLine
                x={markerSnapDate}
                stroke="#dc2626"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                label={{
                  value: markerLabel ?? markerSnapDate,
                  position: 'insideTopRight',
                  fill: '#dc2626',
                  fontSize: 11,
                }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function fmtAxisDate(d: string): string {
  const dt = new Date(d);
  return dt.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

function fmtAxisPrice(pence: number, currency?: string): string {
  if (currency === 'GBP') return `£${(pence / 100).toFixed(2)}`;
  return `${pence.toFixed(0)}p`;
}

function fmtTooltipPrice(pence: number, currency?: string): string {
  if (currency === 'GBP') return `£${(pence / 100).toFixed(4)}`;
  return `${pence.toFixed(4)}p`;
}
